import { ReadAllResponse, uuid } from "@/interfaces";
import { activeOrganizationId } from "@/lib/active-organization";
import { api } from "@/lib/api";
import { INVOICE_PAGE_SIZE_MAX, type InvoiceEmailQueued } from "@shyne/shared/api";
import {
  toInvoiceListQuery,
  toInvoiceListRow,
  toInvoiceRecord,
  toInvoiceStatusChange,
  type ChangedInvoiceStatus,
  type InvoiceListFilters,
  type InvoiceListJoin,
  type InvoiceRecord,
} from "@/repositories/invoice-records";

export const InvoicesRepository = {
  async getById(id: uuid): Promise<InvoiceRecord> {
    return toInvoiceRecord(await api.invoices.get(id, { actingOrganization: activeOrganizationId() }));
  },

  async list(
    page: number = 1,
    pageSize: number = 10,
    filters?: InvoiceListFilters,
    sortKey?: string,
    sortOrder: "asc" | "desc" = "asc"
  ): Promise<ReadAllResponse<InvoiceListJoin>> {
    const query = toInvoiceListQuery(filters, { page, pageSize, sortKey, sortOrder, actingOrganization: activeOrganizationId() });
    const { items, total } = await api.invoices.list(query);
    return { data: items.map(toInvoiceListRow), count: total };
  },

  async listByDailyWorkId(dailyWorkId: uuid): Promise<InvoiceListJoin[]> {
    const { data } = await InvoicesRepository.list(1, INVOICE_PAGE_SIZE_MAX, { contract: dailyWorkId }, "period_start", "asc");
    return data;
  },

  async updateStatus(id: uuid, status: ChangedInvoiceStatus): Promise<InvoiceRecord> {
    return toInvoiceRecord(await api.invoices.setStatus(id, { status: toInvoiceStatusChange(status) }));
  },

  async bulkUpdateStatus(ids: uuid[], status: ChangedInvoiceStatus): Promise<void> {
    if (ids.length === 0) return;
    await api.invoices.setStatuses({ invoices: ids, status: toInvoiceStatusChange(status) });
  },

  async email(id: uuid, email: string): Promise<InvoiceEmailQueued> {
    return api.invoices.email(id, { email });
  },
};
